/* eslint-disable consistent-return */
/* eslint-disable no-param-reassign */
import React, { useEffect, useState } from 'react';
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { OAuthError, useAuth0 } from '@auth0/auth0-react';

import { PUSHER_CONFIG_URL } from 'Services/Applications/Application';
import { LoggerFactory, DOMAIN, FEATURE } from 'Services/Logger';
import { GlobalLoadingCaller } from 'Utils/GlobalLoading';
import { getSessionToken } from 'Utils/session';
import { sendHandleSessionExpiredEvent } from 'Utils/events/flutterWebView';
import { purchaseRedirect } from 'Utils/purchaseRedirect';
import { isMobileDesign, isFromAppMobileWaitSession } from 'Utils/mobile';
import { useSessionErrors } from 'Hooks/useSession/useSessionErrors';
import { isAuthRelatedRoute as isAuthRelatedRouteFn } from './utils';

const Logger = LoggerFactory.getLogger(DOMAIN.CONSUMER_ENABLEMENT);

export const AxiosInterceptor: React.FC = ({ children }) => {
  const {
    getAccessTokenSilently, isAuthenticated, isLoading, loginWithRedirect,
  } = useAuth0();
  const isMobileApp = isMobileDesign();
  const isMobileAppWaitSession = isFromAppMobileWaitSession();
  useSessionErrors();

  const [loadingError, setLoadingError] = useState(false);

  const getAuth0Token = async (): Promise<string | undefined> => getAccessTokenSilently()
    .catch((error) => {
      /**
       * TODO: validate missing_refresh_token errors
       * and handle loginWithRedirect
       */
      if (error instanceof OAuthError) {
        Logger.info(
          'Failed to getAccessTokenSilently from axios interceptor',
          `${DOMAIN.CONSUMER_ENABLEMENT}.${FEATURE.LOGIN}`,
          {
            error,
          },
        );
      }
      return undefined;
    });

  useEffect(() => {
    const reqInterceptor = async (axiosConfig: AxiosRequestConfig & { specialToken?: string }) => {
      if (axiosConfig.specialToken) {
        axiosConfig.headers.Authorization = `Bearer ${axiosConfig?.specialToken}`;
        return axiosConfig;
      }

      if (isMobileAppWaitSession || isAuthenticated) {
        const accessToken = isMobileAppWaitSession ? getSessionToken() : await getAuth0Token();
        axiosConfig.headers.Authorization = `Bearer ${accessToken}`;
      }

      return axiosConfig;
    };

    const interceptor = axios.interceptors.request.use(reqInterceptor);
    return () => axios.interceptors.request.eject(interceptor);
  }, [isAuthenticated, isMobileAppWaitSession]);

  useEffect(() => {
    const resInterceptor = async (response: AxiosResponse) => {
      const { baseURL, url } = response.config;

      /**
         * Kueski Pusher requires Authorization param.
         * Authorization param is added in pusher config request only.
         */
      if (url === `${baseURL}${PUSHER_CONFIG_URL}`) {
        let accessToken: string | null | undefined = getSessionToken();

        if (isAuthenticated) {
          accessToken = await getAuth0Token();
        }

        response.headers.Authorization = `Bearer ${accessToken}`;
      }

      return response;
    };

    const errInterceptor = async (error: AxiosError) => {
      const { config = {} } = error;
      const { baseURL, method, url } = config;

      const excludedCodes = [400, 401, 403, 404, 409, 422];

      if (error.response && !excludedCodes.includes(error.response?.status)) {
        Logger.warn(
          `Axios interceptor error ${baseURL}`,
          `${DOMAIN.CONSUMER_ENABLEMENT}.${FEATURE.LOGIN}`,
          {
            error,
            method,
            url,
          },
        );
      }

      if (error.response && error.response.status === 401) {
        await sendHandleSessionExpiredEvent();

        if (!isMobileAppWaitSession) {
          /**
           * For authentication-related routes and
           * controlled with auth_expired_sessions_handling FF:
           * - Attempt to silently obtain a new access token using Auth0.
           * - If successful, retry the failed request with the new token.
           * - If it fails, redirect to the login page and preserve
           * the current URL to return after login.
           *
           * For other routes:
           * - Trigger the purchaseRedirect flow
           */
          if (isAuthRelatedRouteFn()) {
            setLoadingError(true);
            try {
              const tokenAuth0 = await getAccessTokenSilently({
                cacheMode: 'off',
              });


              error.config.headers.Authorization = `Bearer ${tokenAuth0}`;
              axios.request(config);

              setLoadingError(false);

              return axios.request(config);
            } catch (er) {
              const { origin, pathname, search } = window.location;

              loginWithRedirect({
                appState: { returnTo: origin + pathname + search },
              });

              return Promise.reject(error);
            }
          } else {
            purchaseRedirect();
          }
        }
      }

      return Promise.reject(error);
    };

    const interceptor = axios.interceptors.response.use(resInterceptor, errInterceptor);
    return () => axios.interceptors.response.eject(interceptor);
  }, [isAuthenticated, isMobileAppWaitSession]);

  if ((isLoading || loadingError) && !isMobileApp) return <GlobalLoadingCaller queryKey={['AxiosInterceptor']} />;

  return <>{children}</>;
};
